import { useState, useEffect, useRef } from 'react';

export default function EvolutionSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 } 
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const timelineData = [
    {
      era: "1990s",
      title: "Email, WWW",
      description: "Static pages, blinking GIFs, and loading bars",
      icon: "ri-computer-line",
      color: "from-blue-500 to-purple-600"
    },
    {
      era: "2000s", 
      title: "SEO, E-commerce",
      description: "Search engines and online shopping revolution",
      icon: "ri-shopping-cart-line",
      color: "from-green-500 to-blue-600"
    },
    {
      era: "2010s",
      title: "Social, Mobile",
      description: "Social media boom and mobile-first experiences",
      icon: "ri-smartphone-line",
      color: "from-orange-500 to-red-600"
    },
    {
      era: "2020s",
      title: "Automation, AI",
      description: "Intelligent systems and automated workflows",
      icon: "ri-robot-line",
      color: "from-purple-500 to-pink-600"
    },
    {
      era: "Future",
      title: "Blockchain, Metaverse",
      description: "Decentralized web and virtual experiences",
      icon: "ri-global-line",
      color: "from-cyan-500 to-blue-600"
    }
  ];

  return (
    <section 
      ref={sectionRef}
      id="evolution" 
      className="py-16 md:py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Our Evolution: A Story of{' '}
            <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Digital Dedication
            </span>
          </h2>
          <h3 className="text-xl md:text-3xl font-semibold text-orange-400 mb-4">
            "We've Been Here Since the Dial-Up Days."
          </h3>
          <p className="text-lg md:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Before the likes, comments, and chats – it was all about static pages, blinking GIFs, and loading bars. 
            We lived it, shaped it, and grew with it.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line - Desktop (Center) */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-orange-500 to-red-500 rounded-full"></div>
          
          {/* Timeline Line - Mobile (Left) */}
          <div className="lg:hidden absolute left-4 w-1 h-full bg-gradient-to-b from-orange-500 to-red-500 rounded-full"></div>
          
          <div className="space-y-12 md:space-y-16">
            {timelineData.map((item, index) => (
              <div
                key={index}
                className={`relative flex flex-col lg:flex-row items-center lg:items-center transition-all duration-1000 delay-${index * 100} ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0'
                }`}
              >
                {/* Timeline Node - Desktop */}
                <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full border-4 border-black z-10 animate-pulse"></div>
                
                {/* Timeline Node - Mobile */}
                <div className="lg:hidden absolute left-4 transform -translate-x-1/2 w-4 h-4 bg-gradient-to-r from-orange-500 to-red-500 rounded-full border-2 border-black z-10"></div>
                
                {/* Content */}
                <div className={`w-full lg:w-5/12 pl-12 lg:pl-0 ${
                  index % 2 === 0 ? 'lg:pr-8 lg:text-right' : 'lg:ml-auto lg:pl-8 lg:text-left'
                }`}>
                  <div className="group bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-orange-500/20 hover:border-orange-500/50 transition-all duration-300 hover:transform hover:scale-105">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${item.color} mb-4 group-hover:animate-spin ${index % 2 === 0 ? 'lg:ml-auto lg:mr-0' : 'lg:ml-0 lg:mr-auto'}`}>
                      <i className={`${item.icon} text-2xl text-white`}></i>
                    </div>
                    <h4 className="text-2xl font-bold text-orange-400 mb-2">{item.era}</h4>
                    <h5 className="text-xl font-semibold text-white mb-3">{item.title}</h5>
                    <p className="text-gray-300 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}